import type { ReactNode } from 'react';
import { ChevronLeftIcon } from 'lucide-react';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList } from '@/components/ui/breadcrumb';
import { useNavigate } from '@/lib/legacy';

export type PageHeaderProps = {
  back?: { to: string; label: string };
  title?: ReactNode;
  actions?: ReactNode;
};

/**
 * A page's sticky top band: an opaque 56px strip that clears the app's header, then the back
 * link and any title and actions. The back link goes through navigate.back, so returning to a list
 * restores the history entry (its query string) it came from.
 */
export function PageHeader({ back, title, actions }: PageHeaderProps) {
  const navigate = useNavigate();
  return (
    // z-[1100]: the legacy NavBar's zIndex.header, so page content stacked above the flow (the
    // policy map's Station Status badge) scrolls under the band, not over it.
    <div className="sticky top-0 z-[1100] w-full bg-background">
      <div className="h-14 w-full" />
      <div className="flex min-h-14 w-full items-center justify-between gap-4 px-6 md:px-10">
        <div className="flex min-w-0 items-center gap-4">
          {back ? (
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    href={back.to}
                    className="flex items-center gap-1 font-medium text-foreground"
                    onClick={(event) => {
                      event.preventDefault();
                      navigate.back(back.to);
                    }}
                  >
                    <ChevronLeftIcon aria-hidden className="size-5" />
                    {back.label}
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          ) : null}
          {title}
        </div>
        {actions ? <div className="flex flex-wrap items-center justify-end gap-2">{actions}</div> : null}
      </div>
    </div>
  );
}
